import Button from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { EnfinityPrimaryBtn } from "@/components/design-system/enfinity/EnfinityButton";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { Check, X } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function ToastModal({ showToast, setShowToast, form, type }) {
  const landingPage = import.meta.env.VITE_LANDING_PAGE;

  const { setTheme } = useTheme();

  useEffect(() => {
    if (
      landingPage === "enfinity" ||
      landingPage === "task" ||
      landingPage === "vvv"
    ) {
      setTheme("dark");
    } else {
      setTheme("light");
    }
  }, [landingPage, setTheme]);

  const supportEmail = {
    boost: "support@enfiniboost.com",
    shield: "support@enfinishield.com",
    adcelerate: "support@adcelerate.com",
    enfinity: "request@enfinity.com",
    task: "request@enfinity.com",
    vvv: "support@veryviralvideos.com",
    creatorshield: "support@creatorshield.com",
  };

  const email = supportEmail[landingPage];

  // Don't render anything if showToast is false
  if (!showToast) {
    return null;
  }

  if (!form || (form !== "esign" && form !== "support" && form !== "newsletter")) {
    console.warn("ToastModal: Invalid form type:", form);
    return null;
  }

  return (
    <>
      <Dialog open={showToast} onOpenChange={setShowToast}>
        <DialogContent className={`border-0 rounded-[20px] max-w-md text-center bg-white text-black dark:text-white pt-8 ${landingPage === "enfinity" ? "dark:bg-[#1a1a1a]" : "dark:bg-[#161616]"}`}>
          <DialogTitle className="sr-only">Modal Dialog</DialogTitle>
          <DialogDescription className="sr-only">Modal content</DialogDescription>
          <div className="flex flex-col items-center gap-5 pb-4">
            {/* form === 'esign' */}
            {form === "esign" && (
              <>
                {type === "success" ? (
                  <>
                    <div
                      className={`bg-primary ${
                        landingPage === "enfinity"
                          ? "dark:bg-white dark:text-black"
                          : "dark:bg-primary"
                      } p-3 rounded-full text-center mx-auto text-white`}
                    >
                      <Check />
                    </div>
                    <h2 className="text-lg font-bold"> You’re All Set!</h2>
                    <div className="text-left text-base space-y-4 px-10">
                      <p>
                        Your application has been received. We’ll review your
                        details and contact you soon.
                      </p>
                      <p>
                        Check your inbox for emails from{" "}
                        <span className="text-primary font-bold">{email}</span>{" "}
                        for updates or next steps.{" "}
                      </p>
                      <p>
                        If you don’t see our email within 24 hours, check your
                        spam or promotions folder.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`bg-primary ${
                        landingPage === "enfinity"
                          ? "dark:bg-white dark:text-black"
                          : "dark:bg-primary"
                      } p-3 rounded-full text-center mx-auto text-white`}
                    >
                      <X />
                    </div>
                    <h2 className="text-lg font-bold text-center">
                      Something went wrong.
                    </h2>
                    <div className="text-center text-base space-y-4 px-10">
                      <p>Failed to process your signup.</p>
                      <p>Please try again.</p>
                    </div>
                  </>
                )}
              </>
            )}

            {/* form === 'support' */}
            {form === "support" && (
              <>
                {type === "success" ? (
                  <>
                    <div
                      className={`bg-primary ${
                        landingPage === "enfinity"
                          ? "dark:bg-white dark:text-black"
                          : "dark:bg-primary"
                      } p-3 rounded-full text-center mx-auto text-white`}
                    >
                      <Check />
                    </div>
                    <h2 className="text-lg font-bold">Ticket Submitted!</h2>
                    <div className="text-left text-base space-y-4 px-10">
                      <p>Your support ticket has been received.</p>
                      <p>
                        Check your inbox for emails from{" "}
                        <span className="text-primary font-bold">{email}</span>{" "}
                        for updates or next steps.{" "}
                      </p>
                      <p>
                        If you don’t see our email within 24 hours, check your
                        spam or promotions folder.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`bg-primary ${
                        landingPage === "enfinity"
                          ? "dark:bg-white dark:text-black"
                          : "dark:bg-primary"
                      } p-3 rounded-full text-center mx-auto text-white`}
                    >
                      <X />
                    </div>
                    <h2 className="text-lg font-bold text-center">
                      Something went wrong.
                    </h2>
                    <div className="text-base space-y-4 px-10 text-center">
                      <p>Failed to submit support ticket.</p>
                      <p>Please try again.</p>
                    </div>
                  </>
                )}
              </>
            )}

            {/* form === 'newsletter' */}
            {form === "newsletter" && (
              <>
                {type === "success" ? (
                  <>
                    <div
                      className={`bg-primary ${
                        landingPage === "enfinity"
                          ? "dark:bg-white dark:text-black"
                          : "dark:bg-primary"
                      } p-3 rounded-full text-center mx-auto text-white`}
                    >
                      <Check />
                    </div>
                    <h2 className="text-lg font-normal enfinity-header-font">You're Subscribed!</h2>
                    <div className="text-left text-base space-y-4 px-10">
                      <p className="enfinity-subtext-font font-normal">Thanks for signing up for our newsletter.</p>
                      <p className="enfinity-subtext-font font-normal">
                        Check your inbox for emails from{" "}
                        <span className="font-bold text-white">{email}</span>{" "}
                        for updates and news.
                      </p>
                      <p className="enfinity-subtext-font font-normal">
                        If you don't see our email within 24 hours, check your
                        spam or promotions folder.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`bg-primary ${
                        landingPage === "enfinity"
                          ? "dark:bg-white dark:text-black"
                          : "dark:bg-primary"
                      } p-3 rounded-full text-center mx-auto text-white`}
                    >
                      <X />
                    </div>
                    <h2 className="text-lg font-bold text-center">
                      Something went wrong.
                    </h2>
                    <div className="text-base space-y-4 px-10 text-center">
                      <p>We couldn't complete your subscription.</p>
                      <p>Please try again.</p>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <DialogFooter className="pb-2">
            <DialogClose asChild>
              <Link to={"/"} className="w-1/2 mx-auto">
                {landingPage === "enfinity" ? (
                  <EnfinityPrimaryBtn className="!py-3 w-full" name="Go back to homepage" />
                ) : (
                  <Button className="bg-primary rounded-xl py-5 px-6 text-white">
                    Go back to homepage
                  </Button>
                )}
              </Link>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
